
import React, { useMemo } from 'react';
import { useTranslations } from 'use-intl';
import BankConnectionDialog from '@/components/cash/BankConnectionDialog';
import BankAccountsCard from '@/components/cash/BankAccountsCard';
import WaitingSettlementsBanner from '@/components/bank/WaitingSettlementsBanner';
import { BankAccountSummary, WaitingSettlementsSummary } from "@/types/accounts";

interface AccountsSectionProps {
  accounts: BankAccountSummary[];
  setAccounts: React.Dispatch<React.SetStateAction<BankAccountSummary[]>>;
  handleConnectBank: (newAccounts: BankAccountSummary[]) => Promise<void>;
  isOnboarding?: boolean;
  /** Multi Bank Account (FIN-90) PR4: re-fetches the account list from the server after a
   * mapping change or swap -- the server is authoritative, so the row's own optimistic state
   * is never trusted as the final word. */
  onMappingChanged: () => void | Promise<void>;
  /** FIN-1021: owned by Bank.tsx (useWaitingSettlements), not fetched here directly — its
   * transactionLabels also feed TransactionsSection, so one hook call serves both siblings. */
  waitingSettlements: WaitingSettlementsSummary;
  waitingSettlementsLoading: boolean;
  waitingSettlementsLoadFailed: boolean;
  reloadWaitingSettlements: () => void | Promise<void>;
}

const AccountsSection: React.FC<AccountsSectionProps> = ({
  accounts,
  setAccounts,
  handleConnectBank,
  isOnboarding = false,
  onMappingChanged,
  waitingSettlements,
  waitingSettlementsLoading,
  waitingSettlementsLoadFailed,
  reloadWaitingSettlements,
}) => {
  const t = useTranslations('consultantTax.accounts');
  // FIN-1021/AC14: waiting + mapped-not-retried both mean "this account's mapping still has
  // entries riding on it" — a remap re-arms ALL of them, not only the ones currently unmapped.
  const waitingCountByAccountId = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of [...waitingSettlements.waiting, ...waitingSettlements.mappedNotRetried]) {
      map.set(item.bankAccountId, (map.get(item.bankAccountId) ?? 0) + item.count);
    }
    return map;
  }, [waitingSettlements]);

  const handleMappingChanged = async () => {
    await onMappingChanged();
    await reloadWaitingSettlements();
  };

  return (
    <div className="space-y-4">
      <WaitingSettlementsBanner
        summary={waitingSettlements}
        loading={waitingSettlementsLoading}
        loadFailed={waitingSettlementsLoadFailed}
        onReload={reloadWaitingSettlements}
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold">{t('title')}</h2>
          {/* Multi Bank Account (FIN-90, AC24): a persistent count of accounts needing a
              kontoplan mapping, independent of FIN-578's unbuilt consultant task surface --
              this is the in-app signal AC24 requires to not depend solely on it.
              FIN-1021/AC12: amber via the consultant app's own raw Tailwind palette (matches
              PendingEmailImports.tsx/Overview.tsx) -- the previous text-warning-foreground/
              bg-warning-weak classes are apps/client design-system tokens, undefined here, so
              the pill rendered unstyled grey. */}
          {accounts.filter((a) => !a.chartAccountNumber).length > 0 && (
            <span
              data-testid="accounts-needing-attention-count"
              className="text-sm font-medium text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-2.5 py-0.5"
            >
              {t('needingAttention', { count: String(accounts.filter((a) => !a.chartAccountNumber).length) })}
            </span>
          )}
        </div>

        <BankConnectionDialog
          onConnect={handleConnectBank}
          isOnboarding={isOnboarding}
        />
      </div>

      <BankAccountsCard
        accounts={accounts}
        setAccounts={setAccounts}
        onMappingChanged={handleMappingChanged}
        waitingCountByAccountId={waitingCountByAccountId}
      />
    </div>
  );
};

export default AccountsSection;
